import { useEffect, useState } from 'react';
import { PROVIDER_CHANNELS_MAX_AGE_SECONDS, useGetProviderChannelsQuery } from '@/middlewares/defaultApi';
import { CommsChannel, IntegrationProvider, IntegrationProviderType, MicrosoftTeamsChannel } from '@/types';

type ProviderChannel = CommsChannel | MicrosoftTeamsChannel;

const NO_CHANNELS: ProviderChannel[] = [];

// The backend finishes a large workspace's list in the background, and each poll lets it resume
export const PROVIDER_CHANNELS_INCOMPLETE_POLL_MS = 10_000;

// Slack lists are fetched on demand because large workspaces have too many channels to ship in
// org state; other providers still carry theirs on the provider.
export function useNotificationProviderChannels(
    orgId: string,
    provider?: IntegrationProvider | null,
): ProviderChannel[] {
    const isSlack = provider?.type === IntegrationProviderType.Slack;
    const [isListIncomplete, setIsListIncomplete] = useState(false);
    const { data } = useGetProviderChannelsQuery(
        { orgId, providerId: provider?.id ?? '' },
        {
            skip: !isSlack,
            refetchOnMountOrArgChange: PROVIDER_CHANNELS_MAX_AGE_SECONDS,
            refetchOnFocus: false,
            pollingInterval: isListIncomplete ? PROVIDER_CHANNELS_INCOMPLETE_POLL_MS : 0,
        },
    );
    useEffect(() => {
        setIsListIncomplete(isSlack && data?.data?.isComplete === false);
    }, [isSlack, data]);

    if (isSlack) {
        return data?.data?.channels ?? NO_CHANNELS;
    }
    const providerData = provider?.data as { availableChannels?: ProviderChannel[] } | undefined;
    return providerData?.availableChannels ?? NO_CHANNELS;
}
